/* eslint-disable react/prop-types */
import { FC } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { Link, Logo } from 'cosmos-components';

import { sciBrandNameSelector } from 'src/components/shared/SciPage/SciPage.selector';
import {
    ThlWebsiteUrl,
} from 'src/constants';
import { CaBrandNames, UkBrandNames, UsBrandNames } from 'src/enums';
import { getScreenBreaks } from 'src/utils/htmlHelper';

import Contact from './Contact';
import ContactCA from './ContactCA';
import ContactUK from './ContactUK';
import ContactUS from './ContactUS';
import msg from './footerMessages';

export const Footer: FC = () => {
    const intl = useIntl();
    const currentYear = ` ${new Date().getFullYear()} `;
    const brandName = useSelector(sciBrandNameSelector);
    const isRoadBear = brandName === UsBrandNames.roadBear;
    const isElmonte = brandName === UsBrandNames.elmonte;
    const isCa = brandName === CaBrandNames.canadream;
    const isUk = brandName === UkBrandNames.europe;
    const isUs = isRoadBear || isElmonte;

    return (
        <BrandingSection>
            <BrandingContainer id="footer-branding-container">
                {isUs ? (
                    <ContactUS brandName={brandName as UsBrandNames} />
                ) : isCa ? (
                    <ContactCA />
                ) : isUk ? (
                    <ContactUK />
                ) : (
                    <Contact />
                )} 
                <CopyRightWrapper>
                    <StyledTHLLogoWrapper>
                        <StyledTHLLogo name="thl" isInverted />
                    </StyledTHLLogoWrapper>
                    <CopyRight>
                        {intl.formatMessage(msg.footer.copyRight) +
                            currentYear +
                            intl.formatMessage(msg.footer.Rights)}
                    </CopyRight>
                    <PrivacyPolicy href={ThlWebsiteUrl} target="_blank">
                        {intl.formatMessage(msg.footer.privacyPolicy)}
                    </PrivacyPolicy>
                </CopyRightWrapper>
            </BrandingContainer>
        </BrandingSection>
    );
};

export const BrandingSection = styled.div`
    background-color: ${({ theme: { colors } }) => colors.secondaryBlack};
    margin-left: -0.1rem;
    margin-right: -0.1rem;
    margin-bottom: -0.1rem;
    margin-top: 2rem;
    font-size: 0.9rem;
    color: ${({ theme: { colors } }) => colors.quaternaryGrey};
    padding: 0.5rem 0;
`;

export const BrandingContainer = styled.div`
    display: flex;
    flex-direction: column;
    margin: auto 18rem;
    @media (max-width: ${(props) => getScreenBreaks(props).screenSMax}) {
        margin: auto 7rem;
    }

    @media (min-width: ${(props) =>
            getScreenBreaks(props).screenSMax}) and (max-width: ${(props) =>
            getScreenBreaks(props).screenMDMax}) {
        margin: auto 4.5rem;
    }
`;

export const StyledTHLLogo = styled(Logo)`
    width: 8.7rem;
    height: 4rem;
`;

const logoMixin = css`
    max-width: 9rem;
    width: auto;
    height: 3rem;
`;

export const StyledLogo = styled(Logo)`
    ${logoMixin};
`;

export const StyledJustGoLogoWrapper = styled.div`
    ${logoMixin};
    width: 4rem;
`;

export const CopyRight = styled.div`
    text-align: center;
    text-wrap: nowrap;
    position: relative;
    left: 0;
    right: 0;
    padding-top: 0.2rem;
`;

export const PrivacyPolicy = styled(Link)`
    display: block;
    text-align: center;
    text-wrap: nowrap;
    position: relative;
    left: 0;
    right: 0;
`;

export const EmailUsLink = styled(Link)`
    display: block;
    text-align: center;
    text-wrap: nowrap;
    color: ${({ theme: { colors } }) => colors.white};
    font-size: 1.5rem;
    font-weight: bold;
    &:hover {
        color: ${({ theme: { colors } }) => colors.secondaryRed};
    }
    &:active {
        color: ${({ theme: { colors } }) => colors.secondaryRed};
    }
`;

export const StyledThlMobileGradiant = styled.img`
    height: 3.9rem;
    width: 100%;
`;

export const StyledTHLLogoWrapper = styled.div`
    display: flex;
    align-items: center; 
`;

const CopyRightWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 4rem;
    margin-bottom: 4rem;
    font-size: 1.3rem;

    & > :nth-child(2) {
        margin: 0 22rem;
    }

    @media (max-width: ${(props) => getScreenBreaks(props).screenSMax}) {
        & > :first-child {
            margin: 1rem 0 3rem 0;
        }
        
        & > :nth-child(2) {
            margin-bottom: 0.5rem;
        }

        flex-direction: column;
        align-items: center;
    }
`;

export const NeedHelp = styled.div`
    font-weight: bold;
    font-size: 1.4rem;
`;

export const ContactItemContent = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: nowrap;

    @media (max-width: ${(props) => getScreenBreaks(props).screenSMax}) {
        flex-direction: column;
        gap: 1rem;
    }
`;

export const VerticalSeparator = styled.div`
    width: 2px;
    height: 1.3rem;
    background-color: ${({ theme: { colors } }) => colors.white};
    margin: 0 1rem;
    align-self: center;
    flex-shrink: 0;

    @media (max-width: ${(props) => getScreenBreaks(props).screenSMax}) {
        display: none;
    }
`;
